<template>
	<aside
		id="card-container"
	>
		<StructureCard
			v-for="structure in structuresList"
			:key="structure.id"
			:is-selected="isSelected(structure)"
			:structure="structure"
			@select-structure="handleSelectStructure"
		/>
	</aside>
</template>

<script setup lang="ts">
	import { Structure } from '@/types/structure';
	import StructureCard from './structure-card.vue';
	import { computed, watch, nextTick } from 'vue';
	import { ScrollUtils } from '../../utils/scroll-utils';
	import { isFromCard, selectedStructureId } from '../../store/store';

	const props = defineProps<{
		structuresList: Structure[];
	}>();

	const selectedStructure = computed(() => props.structuresList.find(s => s.id === selectedStructureId.value));

	/**
	 * Scroll sur la carte lié à la structure
	 */
	watch(selectedStructureId, () => scrollTo());

	/**
	 * Reset le scroll lorsque la liste des structures change
	 */
	watch(() => props.structuresList, () => nextTick(() => resetScroll()));

	/**
	 * Gére la sélection de la structure depuis une carte (card)
	 * @param structureId id de la structure
	 */
	function handleSelectStructure(structureId: number) {
		isFromCard.value = true;
		selectedStructureId.value = structureId;
	}

	/**
	 * Détermine si on doit appliquer un style à la carte liée à la structure
	 * @param structure la structure
	 */
	function isSelected(structure: Structure): boolean {
		if (isFromCard.value) {
			return structure.id === selectedStructureId.value;
		}
		return structure.address.latitude === selectedStructure.value?.address.latitude && structure.address.longitude === selectedStructure.value?.address.longitude;
	}

	/**
	 * Réinitialise le scroll à la première structure
	 */
	function resetScroll(): void {
		const cardContainer = document.getElementById('card-container');
		if (cardContainer) {
			if (window.innerWidth > ScrollUtils.BREAK_POINT_WIDTH) {
				cardContainer.scrollTo({
					top: 0,
					behavior: 'smooth'
				});
			} else {
				cardContainer.scrollTo({
					left: 0,
					behavior: 'smooth'
				});
			}
		}
	}

	/**
	 * Scroll jusqu'à la première structure sélectionné
	 */
	function scrollTo(): void {
		let tempStructureId = selectedStructureId.value;

		if (!isFromCard.value) {
			// Si la sélection provient de la map, on va vérifier s'il y a plusieurs structures avec la même adresse
			const sameAddressStructureList = props.structuresList
				.filter(s => s.address.latitude === selectedStructure.value?.address.latitude && s.address.longitude === selectedStructure.value.address.longitude);
			// Si plusieurs structures ont la même adresse, on scroll jusqu'à la première structure trouvée
			if (sameAddressStructureList.length > 1) {
				tempStructureId = sameAddressStructureList[0].id;
			}
		}

		const cardContainer = document.getElementById('card-container');
		if (cardContainer) {
			const card = document.getElementById(`card-${tempStructureId}`);
			if (card) {
				if (window.innerWidth > ScrollUtils.BREAK_POINT_WIDTH) {
					ScrollUtils.scrollToVertical(cardContainer, card);
				} else {
					ScrollUtils.scrollToHorizontal(cardContainer, card);
				}
			}
		}
	}

</script>

<style scoped lang="css">
	#card-container {
		width: 100%;
		display: grid;
		border-width: 1px 0 1px 0;
		border-color: #eee;
		border-style: solid;
		gap: 1rem;
		overflow: auto;
		scroll-snap-type: y mandatory;
	}

	@media screen and (max-width: 768px) {
		#card-container {
			display: flex;
			overflow-y: hidden;
			scroll-snap-type: x mandatory;
			border: none;
		}
	}
</style>
